// DE-WIZARD: the in-memory API's job set-up of a DE (services/api routes/org/de-job.ts), over the
// structure twin's seats, metric definitions, seat metric sets and targets (fake-structure.ts
// mounts it). Same rules as the real routes:
//
// - GET …/digital-employees/{id}/job (managers): the `de_job` fields (defaults and version 0
//   when none is saved), the seat's assigned KPIs with the current weekly target and whether the
//   DE reports each one itself, and post approval from the seat's settings.
// - PUT (managers): only what is sent changes. `kpis` adds or updates KPIs on the DE's seat (a
//   new one is scored at weight 1; a target replaces the current weekly target from today);
//   `remove_metric_def_ids` removes KPIs explicitly, never a KPI left out; `post_approval` writes
//   the seat's `require_approval` / `message_approver_seat_id`; `home_channel_id` must be a
//   channel of the company; `submit` ends Draft. Readiness (fake-data.ts `deReadiness`) reads it.
//   A KPI joining the reporting (or changing its duty) sets the seat's `agent_tool` reporting
//   duty in the reporting twin; one leaving it removes that duty while it is `agent_tool`.
import type { Handler, Principal } from "./fake";
import type { FakeCompany, FakeDe, FakeDeJob } from "./fake-data";
import { uid } from "./fake-data";
import type { AgentJobDuty } from "./fake-reporting";
import type { MetricDef, Seat, SeatAssignment, SeatMetric, TargetRule } from "./types";

/** A stored seat-set line (the read adds the metric's key and name, KPI-FIX). */
export type FakeSeatMetricItem = Omit<
  SeatMetric,
  | "company_id"
  | "seat_id"
  | "metric_key"
  | "metric_name"
  | "actor_kind"
  | "actor_membership_id"
  | "created_at"
  | "updated_at"
>;

export type DeJobDeps = {
  json: (body: unknown, status?: number) => Response;
  problem: (status: number, code: never, title: string, detail?: string) => Response;
  notFound: () => Response;
  validation: (detail: string) => Response;
  now: () => Date;
  nextSeq: () => number;
  desOf: (companyId: string) => FakeDe[];
  seatsOf: (company: FakeCompany) => Seat[];
  assignmentsOf: (company: FakeCompany) => SeatAssignment[];
  metricsOf: (company: FakeCompany) => MetricDef[];
  targetsOf: (company: FakeCompany) => TargetRule[];
  /** The seat's metric set: the stored one, else the seeded scorecard's (null: none yet). */
  seatMetricsOf: (p: Principal, seatId: string) => FakeSeatMetricItem[];
  setSeatMetrics: (seatId: string, items: FakeSeatMetricItem[]) => void;
  /** Whether `id` is a chat channel of the company (not a DM, not archived); absent: any. */
  channelOk?: (p: Principal, id: string) => boolean;
  /** AGENT-REPORTING: the reporting twin's duties the job sets and removes; absent: none. */
  agentDuty?: AgentJobDuty;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DECIMAL = /^-?\d{1,12}(\.\d{1,6})?$/;
const DUTIES = ["daily", "weekly", "monthly"] as const;
type Duty = (typeof DUTIES)[number];
const PUT_KEYS = [
  "path",
  "submit",
  "home_channel_id",
  "kpis",
  "remove_metric_def_ids",
  "post_approval",
];

const list = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

export function deJobHandlers(d: DeJobDeps): Partial<Record<string, Handler>> {
  const deIn = (p: Principal, id: string | undefined) =>
    d.desOf(p.company.id).find((x) => x.membership_id === id) ?? null;
  const seatOf = (p: Principal, de: FakeDe) =>
    de.status === "retired" || de.removed_at
      ? null
      : (d.seatsOf(p.company).find((s) => s.id === de.seat_id) ?? null);
  const today = () => d.now().toISOString().slice(0, 10);

  /** The current weekly target of a KPI on the seat (open-ended, newest first). */
  const targetOf = (p: Principal, seatId: string, metricId: string) =>
    d
      .targetsOf(p.company)
      .filter(
        (t) =>
          t.subject_kind === "seat" &&
          t.subject_id === seatId &&
          t.metric_def_id === metricId &&
          t.period_type === "week" &&
          t.effective_to === null,
      )
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] ?? null;

  function view(p: Principal, de: FakeDe) {
    const seat = seatOf(p, de);
    const job = de.job;
    const defs = new Map(d.metricsOf(p.company).map((m) => [m.id, m]));
    const kpis = seat
      ? [...d.seatMetricsOf(p, seat.id)]
          .sort((a, b) => a.position - b.position)
          .flatMap((m) => {
            const def = defs.get(m.metric_def_id);
            if (!def) return [];
            const r = job?.reporting.find((x) => x.metric_def_id === m.metric_def_id);
            return [
              {
                metric_def_id: def.id,
                key: def.key,
                name: def.name,
                unit: def.unit,
                tracking_mode: m.tracking_mode,
                weekly_target: targetOf(p, seat.id, def.id)?.value ?? null,
                reported_by_agent: Boolean(r),
                duty: r?.duty ?? null,
              },
            ];
          })
      : [];
    const settings = (seat?.settings ?? {}) as Record<string, unknown>;
    const approver = settings.message_approver_seat_id;
    return {
      de_membership_id: de.membership_id,
      seat_id: seat?.id ?? null,
      path: job?.path ?? "done_for_you",
      submitted_at: job?.submitted_at ?? null,
      home_channel_id: job?.home_channel_id ?? null,
      reporting: job?.reporting ?? [],
      kpis,
      post_approval: {
        required:
          list(settings.require_approval).includes("message") &&
          !list(settings.auto_approve).includes("message"),
        approver_seat_id: typeof approver === "string" ? approver : null,
      },
      version: job?.version ?? 0,
      can_edit: p.manager,
    };
  }

  return {
    "digitalEmployees.job": ({ principal: p, params }) => {
      if (!p) return d.notFound();
      if (!p.manager) return d.problem(403, "forbidden" as never, "Forbidden");
      const de = deIn(p, params.id);
      if (!de) return d.notFound();
      return d.json(view(p, de));
    },
    "digitalEmployees.putJob": ({ principal: p, params, body }) => {
      if (!p) return d.notFound();
      if (!p.manager) return d.problem(403, "forbidden" as never, "Forbidden");
      const de = deIn(p, params.id);
      if (!de) return d.notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const extra = Object.keys(b).find((k) => !PUT_KEYS.includes(k));
      if (extra) return d.validation(`${extra}: unrecognized key`);
      if (b.path !== undefined && b.path !== "done_for_you" && b.path !== "self_serve")
        return d.validation("path: done_for_you or self_serve");
      if (b.submit !== undefined && b.submit !== true) return d.validation("submit: true");
      const home = b.home_channel_id;
      if (home !== undefined && home !== null) {
        if (typeof home !== "string" || !UUID.test(home))
          return d.validation("home_channel_id: must be a UUID");
        if (d.channelOk && !d.channelOk(p, home))
          return d.validation("home_channel_id: no such channel in this company");
      }
      const seat = seatOf(p, de);
      const kpis = b.kpis as
        | {
            metric_def_id?: unknown;
            weekly_target?: unknown;
            reported_by_agent?: unknown;
            duty?: unknown;
          }[]
        | undefined;
      const removals = b.remove_metric_def_ids as unknown[] | undefined;
      const approval = b.post_approval as
        | { required?: unknown; approver_seat_id?: unknown }
        | undefined;
      if ((kpis?.length || removals?.length || approval) && !seat)
        return d.validation("the digital employee has no active seat");
      const defs = new Map(d.metricsOf(p.company).map((m) => [m.id, m]));
      if (kpis !== undefined) {
        if (!Array.isArray(kpis)) return d.validation("kpis: an array");
        for (const [n, k] of kpis.entries()) {
          const def = typeof k.metric_def_id === "string" ? defs.get(k.metric_def_id) : undefined;
          if (!def || def.archived_at)
            return d.validation(`kpis.${n}.metric_def_id: no such metric`);
          if (typeof k.reported_by_agent !== "boolean")
            return d.validation(`kpis.${n}.reported_by_agent: a boolean`);
          if (k.duty !== undefined && !DUTIES.includes(k.duty as Duty))
            return d.validation(`kpis.${n}.duty: daily, weekly or monthly`);
          const t = k.weekly_target;
          if (
            t !== undefined &&
            t !== null &&
            !(typeof t === "number" && Number.isFinite(t)) &&
            !(typeof t === "string" && DECIMAL.test(t))
          )
            return d.validation(`kpis.${n}.weekly_target: a decimal`);
        }
      }
      if (
        removals !== undefined &&
        (!Array.isArray(removals) || removals.some((r) => !UUID.test(String(r))))
      )
        return d.validation("remove_metric_def_ids: UUIDs");
      if (approval !== undefined) {
        if (typeof approval.required !== "boolean")
          return d.validation("post_approval.required: a boolean");
        const a = approval.approver_seat_id;
        if (a !== null) {
          const s = d.seatsOf(p.company).find((x) => x.id === a);
          const person = d
            .assignmentsOf(p.company)
            .some((x) => x.seat_id === a && x.active_to === null && x.membership_kind === "person");
          if (!s || s.status === "closed" || !person)
            return d.validation("post_approval.approver_seat_id: an open seat with a person on it");
        }
      }

      const at = d.now().toISOString();
      const job: FakeDeJob = de.job ?? {
        path: "done_for_you",
        submitted_at: null,
        home_channel_id: null,
        reporting: [],
        version: 0,
        updated_at: at,
      };
      let changed = false;
      if (b.path !== undefined && b.path !== job.path) {
        job.path = b.path as FakeDeJob["path"];
        changed = true;
      }
      if (b.submit === true && !job.submitted_at) {
        job.submitted_at = at;
        changed = true;
      }
      if (home !== undefined && home !== job.home_channel_id) {
        job.home_channel_id = home as string | null;
        changed = true;
      }
      if (seat && (kpis?.length || removals?.length)) {
        let set = [...d.seatMetricsOf(p, seat.id)];
        let reporting = [...job.reporting];
        for (const k of kpis ?? []) {
          const id = k.metric_def_id as string;
          if (!set.some((m) => m.metric_def_id === id))
            set.push({
              metric_def_id: id,
              kpi_weight: "1",
              goal_weight: "0",
              tracking_mode: "scored",
              penalty_only: false,
              position: set.reduce((n, m) => Math.max(n, m.position + 1), 0),
            });
          const t = k.weekly_target;
          if (t !== undefined && t !== null) {
            const value = String(Number(t));
            const current = targetOf(p, seat.id, id);
            if (!current || Number(current.value) !== Number(value)) {
              if (current) current.effective_to = today();
              d.targetsOf(p.company).push({
                company_id: p.company.id,
                id: uid(0x7b00, d.nextSeq()),
                metric_def_id: id,
                subject_kind: "seat",
                subject_id: seat.id,
                period_type: "week",
                value,
                value_max: null,
                effective_from: today(),
                effective_to: null,
                actor_kind: "person",
                actor_membership_id: null,
                created_at: at,
                updated_at: at,
              } as TargetRule);
            }
          }
          const before = JSON.stringify(reporting);
          const prior = reporting.find((r) => r.metric_def_id === id);
          reporting = reporting.filter((r) => r.metric_def_id !== id);
          if (k.reported_by_agent)
            reporting.push({
              metric_def_id: id,
              duty: (k.duty as Duty | undefined) ?? prior?.duty ?? "weekly",
            });
          if (JSON.stringify(reporting) !== before) changed = true;
        }
        const gone = new Set((removals ?? []).map(String));
        if (gone.size) {
          set = set.filter((m) => !gone.has(m.metric_def_id));
          const kept = reporting.filter((r) => !gone.has(r.metric_def_id));
          if (kept.length !== reporting.length) changed = true;
          reporting = kept;
        }
        d.setSeatMetrics(seat.id, set);
        // AGENT-REPORTING: the duties the job's reporting stands for (as the API's de-job.ts).
        const was = new Map(job.reporting.map((r) => [r.metric_def_id, r.duty]));
        const now = new Map(reporting.map((r) => [r.metric_def_id, r.duty]));
        for (const [id, duty] of now)
          if (was.get(id) !== duty) d.agentDuty?.set(p.company, seat.id, id, duty);
        for (const id of was.keys()) if (!now.has(id)) d.agentDuty?.remove(p.company, seat.id, id);
        job.reporting = reporting;
      }
      if (seat && approval) {
        const s = { ...((seat.settings ?? {}) as Record<string, unknown>) };
        const held = list(s.require_approval).filter((x) => x !== "message");
        s.require_approval = approval.required ? [...held, "message"] : held;
        if (approval.required) s.auto_approve = list(s.auto_approve).filter((x) => x !== "message");
        if (approval.approver_seat_id) s.message_approver_seat_id = approval.approver_seat_id;
        else delete s.message_approver_seat_id;
        Object.assign(seat, { settings: s, updated_at: at });
      }
      if (changed) {
        job.version += 1;
        job.updated_at = at;
      }
      if (changed || de.job) de.job = job;
      return d.json(view(p, de));
    },
  };
}
